import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    AbstractControl,
    ControlContainer,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    FormGroupDirective,
    Validators
} from '@angular/forms';
import { Region, SeriousIncidentType } from '@app/api-module/control-centre-api-client';
import { AuthenticationService } from '@app/authentication/services/authentication.service';
import { FeedbackTypeOption, FormElementModel } from '@app/clinical-feedback/clinical-feedback.model';
import { ContactPersonFieldType } from '@app/clinical-feedback/components/contact-person/contact-person.component';
import { contactPersonRowValidator } from '@app/clinical-feedback/components/contact-person/contact-person.directive';
import { User } from '@app/models';
import { Subscription, combineLatest } from 'rxjs';
import { ClinicalFeedbackConstants } from 'src/app/clinical-feedback/clinical-feedback-constants';
import { NewClinicalFeedbackFormHelper } from '../../new-clinical-feedback.helper';

@Component({
    selector: 'app-contact-detail-form',
    templateUrl: './contact-detail-form.component.html',
    styleUrls: [],
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class ContactDetailFormComponent implements OnInit, OnDestroy {
    @Input() region?: Region;

    formControlName = 'contactDetails';
    formGroup!: UntypedFormGroup;
    private readonly subscriptions = new Subscription();

    formElementModels = {
        userAddingFeedback: new FormElementModel({
            id: 'userAddingFeedback'
        }),
        otherContacts: new FormElementModel({
            id: 'otherContacts'
        }),
        SIOwnerContact: new FormElementModel({
            id: 'SIOwnerContact'
        }),
        patientSafetyLead: new FormElementModel({
            id: 'patientSafetyLead'
        })
    };

    contactSectionLabelUki = 'SI/PSI Owner/Sponsor';
    contactSectionLabelAnz = 'SI Owner/Sponsor';

    clinicalFeedbackConstants = ClinicalFeedbackConstants;
    user!: User | null;
    showConfirmedSeriousIncidentFields = false;

    constructor(
        public parent: FormGroupDirective,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly authenticationService: AuthenticationService
    ) {}

    ngOnInit(): void {
        this.user = this.authenticationService.getCurrentUser();
        this.formGroup = this.formBuilder.group({
            userAddingFeedbackName: [{ value: this.user?.firstName + ' ' + this.user?.surname, disabled: true }],
            userAddingFeedbackEmail: [{ value: this.user?.email, disabled: true }],
            userAddingFeedbackPhone: [
                { value: NewClinicalFeedbackFormHelper.GetFormattedPhoneNumber(this.user?.countryCode, this.user?.number), disabled: true }
            ],
            userAddingFeedbackPhoneExt: [{ value: this.user?.extension, disabled: true }],
            SIOwnerContact: this.formBuilder.group(
                {
                    SIOwnerContactName: this.personControl(ContactPersonFieldType.Name, this.region === Region.Uki),
                    SIOwnerContactEmail: this.personControl(ContactPersonFieldType.Email, this.region === Region.Uki),
                    SIOwnerContactPhone: this.personControl(ContactPersonFieldType.Phone),
                    SIOwnerContactPhoneExt: this.personControl(ContactPersonFieldType.PhoneExt)
                },
                {
                    validators: contactPersonRowValidator('SIOwnerContact', this.region === Region.Uki),
                    updateOn: 'blur'
                }
            ),
            patientSafetyLead: this.formBuilder.group(
                {
                    patientSafetyLeadName: this.personControl(ContactPersonFieldType.Name, this.region === Region.Uki),
                    patientSafetyLeadEmail: this.personControl(ContactPersonFieldType.Email, this.region === Region.Uki),
                    patientSafetyLeadPhone: this.personControl(ContactPersonFieldType.Phone),
                    patientSafetyLeadPhoneExt: this.personControl(ContactPersonFieldType.PhoneExt)
                },
                {
                    validators: contactPersonRowValidator('patientSafetyLead', this.region === Region.Uki),
                    updateOn: 'blur'
                }
            ),
            otherContacts: this.formBuilder.array([this.newPerson()])
        });
        this.patientSafetyLeadGroup.disable();
        this.SIOwnerContactGroup.disable();
        this.parent.form.addControl(this.formControlName, this.formGroup);

        this.handleFormValueChanges();
    }

    handleFormValueChanges() {
        this.subscriptions.add(
            combineLatest([
                this.feedbackForm['feedbackType'].valueChanges,
                this.feedbackForm['seriousIncidentType'].valueChanges
            ]).subscribe(([feedbackTypeValue, seriousIncidentTypeValue]) => {
                if (
                    feedbackTypeValue === FeedbackTypeOption.AdvisedSeriousIncident &&
                    (seriousIncidentTypeValue === SeriousIncidentType.ConfirmedSeriousIncident ||
                        seriousIncidentTypeValue === SeriousIncidentType.AimsIms)
                ) {
                    this.showConfirmedSeriousIncidentFields = true;
                    this.patientSafetyLeadGroup.enable();
                    this.SIOwnerContactGroup.enable();
                    this.removeControlErrors([this.SIOwnerContactGroup, this.patientSafetyLeadGroup]);
                } else {
                    this.showConfirmedSeriousIncidentFields = false;
                    this.patientSafetyLeadGroup.disable();
                    this.SIOwnerContactGroup.disable();
                }
            })
        );
    }

    get feedbackForm() {
        return (this.parent.form.get('feedback') as UntypedFormGroup).controls;
    }

    get otherContactsControls(): UntypedFormGroup[] {
        return this.otherContacts.controls as UntypedFormGroup[];
    }

    get otherContacts(): UntypedFormArray {
        return this.formGroup.controls['otherContacts'] as UntypedFormArray;
    }

    get patientSafetyLeadGroup() {
        return this.formGroup.get('patientSafetyLead') as UntypedFormGroup;
    }

    get SIOwnerContactGroup() {
        return this.formGroup.get('SIOwnerContact') as UntypedFormGroup;
    }

    get isRegionUki() {
        return this.region === Region.Uki;
    }

    private removeControlErrors(controls: AbstractControl[]) {
        controls.forEach(control => {
            if (control instanceof UntypedFormGroup) {
                Object.keys(control.controls).forEach(key => {
                    const groupControl = control.get(key);
                    this.removeControlErrors([groupControl as AbstractControl]);
                });
            } else if (Object.keys(control.errors ?? {}).length) {
                control.setErrors(null);
                control.markAsUntouched();
            }
        });
    }

    private newPerson(): UntypedFormGroup {
        return this.formBuilder.group(
            {
                otherContactsName: this.personControl(ContactPersonFieldType.Name),
                otherContactsEmail: this.personControl(ContactPersonFieldType.Email),
                otherContactsPhone: this.personControl(ContactPersonFieldType.Phone),
                otherContactsPhoneExt: this.personControl(ContactPersonFieldType.PhoneExt)
            },
            {
                validators: contactPersonRowValidator('otherContacts'),
                updateOn: 'blur'
            }
        );
    }

    private personControl(field: ContactPersonFieldType, required = false) {
        let formcontrol = new UntypedFormControl();
        switch (field) {
            case ContactPersonFieldType.Name:
                formcontrol = new UntypedFormControl('', [
                    Validators.pattern(ClinicalFeedbackConstants.NAME_PATTERN),
                    Validators.maxLength(ClinicalFeedbackConstants.VALIDATOR_MAX_LENGTH_128)
                ]);
                if (required) {
                    formcontrol.addValidators(Validators.required);
                }
                break;
            case ContactPersonFieldType.Email:
                formcontrol = new UntypedFormControl('', [
                    Validators.email,
                    Validators.maxLength(ClinicalFeedbackConstants.VALIDATOR_MAX_LENGTH_128)
                ]);
                if (required) {
                    formcontrol.addValidators(Validators.required);
                }
                break;
            case ContactPersonFieldType.Phone:
                formcontrol = new UntypedFormControl('', [
                    Validators.pattern(ClinicalFeedbackConstants.PHONE_PATTERN),
                    Validators.maxLength(ClinicalFeedbackConstants.VALIDATOR_MAX_LENGTH_32)
                ]);
                break;
            case ContactPersonFieldType.PhoneExt:
                formcontrol = new UntypedFormControl('', [
                    Validators.pattern(ClinicalFeedbackConstants.PHONE_EXTENSION_PATTERN),
                    Validators.maxLength(ClinicalFeedbackConstants.VALIDATOR_MAX_LENGTH_PHONE_EXTENSION)
                ]);
                break;
        }
        return formcontrol;
    }

    addOtherContact() {
        this.otherContacts.push(this.newPerson());
    }

    deleteOtherContact(index: number) {
        if (this.otherContactsControls.length <= 1) {
            return;
        }

        this.otherContacts.removeAt(index);
        this.otherContacts.markAsDirty();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
